import React, {useEffect, useState, useMemo, useCallback, useRef} from 'react';
import {Dispatch, bindActionCreators} from 'redux';
import {FormattedMessage, injectIntl} from 'react-intl';
import {connect} from 'react-redux';
import {RootState} from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as clientActions from '../../actions/client';
import * as measuresActions from '../../actions/measures';
import {useHistory, withRouter} from 'react-router';
import {ClientObject} from '../../types/client.type';
import {useParams} from "react-router-dom";
import {ParameterTypes} from "../../types/parameter.type";
import {useUrlFilters} from '../../hooks/useUrlFilters';

import {translatedText} from '../../components/TranslatedText';
import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaTable, {MikaColumn} from '../../components/MikaTable/MikaTable';
import MikaViewToggle from '../../components/MikaViewToggle/MikaViewToggle';
import MikaDateFilter, {DateRange} from '../../components/MikaDateFilter/MikaDateFilter';
import MikaConfirmModal from '../../components/MikaConfirmModal/MikaConfirmModal';
import PaginationComponent from '../../components/Pagination/PaginationComponent';
import '../../components/Pagination/Pagination.scss';
import ContactDashboardStrip from '../Contact/ContactDashboardStrip';
import ContactCardGrid from '../Contact/ContactCardGrid';
import {getClientStatus, getProjectCount, formatDate, CLIENT_STATUS_I18N_KEY} from '../Contact/contactHelpers';

interface Props {
    intl: any
    history: any
    location: any
    clientActions: any
    measuresActions: any
    measures: any
    actions: any
    letter: string
    loading: boolean
    archiveRecords: any
    archiveRecordsPagination: any
    permissions: any
    buttonLoader: any
}

// ─── Column definitions per module ──────────────────────────────────

const ARCHIVE_COLUMNS: Record<string, MikaColumn[]> = {
    contact: [
        {key: 'id', title: '#', sortable: true, width: '50px', render: (val: any) => <span style={{color: '#94a3b8', fontSize: '11px'}}>{val}</span>},
        {key: 'firstnameAndLastname', title: translatedText({id:"fullName",defaultMessage:"Emri i plotë"}), sortable: true, render: (val: any, row: any) => `${val || ''} ${row.lastName || ''}`.trim() || '\u2014'},
        {key: 'phoneNumber', title: translatedText({id:"phone",defaultMessage:"Telefoni"})},
        {key: 'email', title: translatedText({id:"email",defaultMessage:"Email"}), hideOnMobile: true},
        {key: 'city', title: translatedText({id:"city",defaultMessage:"Qyteti"}), sortable: true, hideOnMobile: true},
        {key: 'companyName', title: translatedText({id:"company",defaultMessage:"Kompania"}), sortable: true, hideOnMobile: true},
        {key: 'projects', title: translatedText({id:"projects",defaultMessage:"Projekte"}), render: (_: any, row: any) => {
            const count = getProjectCount(row);
            return count > 0
                ? <span className="clp-badge clp-badge-info">{count}</span>
                : <span className="clp-badge clp-badge-muted">0</span>;
        }},
        {key: 'status', title: translatedText({id:"status",defaultMessage:"Statusi"}), render: (_: any, row: any) => {
            const s = getClientStatus(row);
            return <span className="clp-status" style={{color: s.color, background: s.bg}}><FormattedMessage id={CLIENT_STATUS_I18N_KEY[s.variant]} defaultMessage={s.variant}/></span>;
        }},
        {key: 'createdAt', title: translatedText({id:"date",defaultMessage:"Data"}), sortable: true, hideOnMobile: true, render: (val: any) => formatDate(val)},
        {key: 'user.userName', title: translatedText({id:"agent",defaultMessage:"Agjenti"}), hideOnMobile: true, render: (val: any) => val || '\u2014'},
    ],
    measures: [
        {key: 'id', title: translatedText({id:"projectNumber",defaultMessage:"Numri i projektit"}), hideOnMobile: true},
        {key: 'project.label', title: translatedText({id:"projectName",defaultMessage:"Emri i projektit"})},
        {key: 'clientName', title: translatedText({id:"clientName",defaultMessage:"Emri i klientit"})},
        {key: 'productLocation', title: translatedText({id:"location",defaultMessage:"Lokacioni"}), hideOnMobile: true},
        {key: 'measureDate', title: translatedText({id:"measureDate",defaultMessage:"Data e matjes"}), hideOnMobile: true},
    ],
    offers: [
        {key: 'id', title: translatedText({id:"projectNumber",defaultMessage:"Numri i projektit"}), hideOnMobile: true},
        {key: 'clientNameAndLastName', title: translatedText({id:"clientName",defaultMessage:"Emri i klientit"}), hideOnMobile: true},
        {key: 'project.label', title: translatedText({id:"projectName",defaultMessage:"Emri i projektit"})},
        {key: 'offerDate', title: translatedText({id:"offerDate",defaultMessage:"Data e ofertës"}), hideOnMobile: true},
        {key: 'offerStatus.label', title: translatedText({id:"offerStatus",defaultMessage:"Statusi i ofertës"}), hideOnMobile: true},
        {key: 'offerTotalCalculation', title: translatedText({id:"offerTotal",defaultMessage:"Totali i ofertës"}), hideOnMobile: true},
    ],
    agreements: [
        {key: 'id', title: translatedText({id:"projectNumber",defaultMessage:"Numri i projektit"}), hideOnMobile: true},
        {key: 'clientNameAndLastName', title: translatedText({id:"clientName",defaultMessage:"Emri i klientit"}), hideOnMobile: true},
        {key: 'project.label', title: translatedText({id:"projectName",defaultMessage:"Emri i projektit"})},
        {key: 'agreementStatus.label', title: translatedText({id:"status",defaultMessage:"Statusi"}), hideOnMobile: true},
        {key: 'total', title: translatedText({id:"agreementTotal",defaultMessage:"Totali i marrëveshjes"}), hideOnMobile: true},
        {key: 'totalDebtLeft', title: translatedText({id:"remainingTotal",defaultMessage:"Totali i mbetur"}), hideOnMobile: true},
    ],
    warehouse: [
        {key: 'id', title: translatedText({id:"depositNumber",defaultMessage:"Numri i deponimit"}), hideOnMobile: true},
        {key: 'label', title: translatedText({id:"designation",defaultMessage:"Emërtimi"})},
        {key: 'project.projectName', title: translatedText({id:"projectName",defaultMessage:"Emri i projektit"})},
        {key: 'client.name', title: translatedText({id:"clientName",defaultMessage:"Emri i klientit"}), hideOnMobile: true},
        {key: 'user.label', title: translatedText({id:"user",defaultMessage:"Përdoruesi"}), hideOnMobile: true},
    ],
    transport: [
        {key: 'id', title: translatedText({id:"depositNumber",defaultMessage:"Numri i deponimit"}), hideOnMobile: true},
        {key: 'label', title: translatedText({id:"designation",defaultMessage:"Emërtimi"})},
        {key: 'clientName', title: translatedText({id:"clientName",defaultMessage:"Emri i klient"}), hideOnMobile: true},
        {key: 'projectName', title: translatedText({id:"projectName",defaultMessage:"Emri i projektit"})},
        {key: 'city', title: translatedText({id:"city",defaultMessage:"Qyteti"}), hideOnMobile: true},
        {key: 'country', title: translatedText({id:"country",defaultMessage:"Shteti"}), hideOnMobile: true},
        {key: 'address', title: translatedText({id:"address",defaultMessage:"Adresa"}), hideOnMobile: true},
        {key: 'measureComment', title: translatedText({id:"measureComment",defaultMessage:"Komenti i matjes"}), hideOnMobile: true},
        {key: 'transport.orderAcceptanceDate', title: translatedText({id:"departureDate",defaultMessage:"Data e nisjes"}), hideOnMobile: true},
        {key: 'transport.orderArrivalDate', title: translatedText({id:"arrivalDate",defaultMessage:"Data e arritjes"}), hideOnMobile: true},
    ],
    installation: [
        {key: 'id', title: translatedText({id:"installationNumber",defaultMessage:"Numri i instalimit"}), hideOnMobile: true},
        {key: 'project.label', title: translatedText({id:"projectName",defaultMessage:"Emri i projektit"})},
        {key: 'project.projectComment', title: translatedText({id:"projectComment",defaultMessage:"Komenti i projektit"}), hideOnMobile: true},
        {key: 'project.projectLocation', title: translatedText({id:"projectLocation",defaultMessage:"Lokacioni i projektit"})},
    ],
};

const MODULE_LABELS: Record<string, string> = {
    contact: translatedText({id:"clients",defaultMessage:"klientë"}), measures: translatedText({id:"measuresPl",defaultMessage:"matje"}), offers: translatedText({id:"offersPl",defaultMessage:"oferta"}), agreements: translatedText({id:"agreementsPl",defaultMessage:"marrëveshje"}),
    warehouse: translatedText({id:"warehousePl",defaultMessage:"depo"}), transport: translatedText({id:"transportsPl",defaultMessage:"transporte"}), installation: translatedText({id:"installationsPl",defaultMessage:"instalime"}), service: translatedText({id:"servicesPl",defaultMessage:"servise"}),
};

const MODULE_TITLES: Record<string, string> = {
    contact: translatedText({id:"contactArchive",defaultMessage:"Arkiva e kontakteve"}), measures: translatedText({id:"measuresArchive",defaultMessage:"Arkiva e matjeve"}), offers: translatedText({id:"offersArchive",defaultMessage:"Arkiva e ofertave"}),
    agreements: translatedText({id:"agreementsArchive",defaultMessage:"Arkiva e marrëveshjeve"}), warehouse: translatedText({id:"warehouseArchive",defaultMessage:"Arkiva e depos"}), transport: translatedText({id:"transportArchive",defaultMessage:"Arkiva e transporteve"}),
    installation: translatedText({id:"installationArchive",defaultMessage:"Arkiva e instalimeve"}), service: translatedText({id:"serviceArchive",defaultMessage:"Arkiva e serviseve"}),
};

type StripFilter = 'total' | 'new' | 'cities' | 'agents';

const Archive = (props: Props) => {
    const history = useHistory();
    const {moduleName} = useParams<ParameterTypes>();
    const isContact = moduleName === 'contact';

    const {filters, setFilter, setFilters, replaceFilters} = useUrlFilters(props.history, props.location, {
        page: {default: 1, type: 'number'},
        perPage: {default: 20, type: 'number'},
        search: {default: '', type: 'string'},
        strip: {default: 'total', type: 'string'},
        dateFrom: {default: '', type: 'string'},
        dateTo: {default: '', type: 'string'},
        city: {default: '', type: 'string'},
        agent: {default: '', type: 'string'},
    });

    const currentPage = filters.page as number;
    const perPage = filters.perPage as number;
    const searchText = filters.search as string;
    const stripFilter = (filters.strip || 'total') as StripFilter;
    const dateRange: DateRange | null = filters.dateFrom && filters.dateTo ? {from: filters.dateFrom, to: filters.dateTo} : null;
    const activeCity = filters.city || null;
    const activeAgentId = filters.agent ? Number(filters.agent) : null;

    const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
    const [deleteId, setDeleteId] = useState<number>(null);
    const [deleteRecord, setDeleteRecord] = useState<any>(null);

    // Contact archive specific state
    const [statsData, setStatsData] = useState<any>(null);
    const [cityPopoverOpen, setCityPopoverOpen] = useState(false);
    const [agentPopoverOpen, setAgentPopoverOpen] = useState(false);
    const [cityPopoverPos, setCityPopoverPos] = useState({top: 0, right: 0});
    const [agentPopoverPos, setAgentPopoverPos] = useState({top: 0, right: 0});
    const cityRef = useRef<HTMLDivElement>(null);
    const agentRef = useRef<HTMLDivElement>(null);
    const stripRef = useRef<HTMLDivElement>(null);

    // Derive activeAgent object from agentDistribution + URL id
    const agentDistribution: {id: number; name: string; count: number}[] = useMemo(() => {
        return statsData?.agentDistribution || [];
    }, [statsData]);
    const activeAgent = useMemo(() => {
        if (!activeAgentId) return null;
        const found = agentDistribution.find(a => a.id === activeAgentId);
        return found ? {id: found.id, name: found.name} : null;
    }, [activeAgentId, agentDistribution]);

    const columns = useMemo(() => ARCHIVE_COLUMNS[moduleName] || ARCHIVE_COLUMNS.contact, [moduleName]);

    // Build effective search
    const getEffectiveSearch = (search: string, city: string | null) => {
        if (city && search) return `${city} ${search}`;
        if (city) return city;
        return search;
    };

    // Fetch data
    const fetchArchive = () => {
        const effectiveSearch = getEffectiveSearch(searchText, activeCity);
        const extra = stripFilter === 'new' ? 'new_this_month=true' : '';
        props.actions.getArchiveRecords(
            `archive/${moduleName}`,
            dateRange?.from || null,
            dateRange?.to || null,
            currentPage, perPage, effectiveSearch, extra
        );
    };

    // Fetch stats on mount (contact archive only)
    useEffect(() => {
        if (isContact) {
            props.clientActions.getArchiveDashboardStats().then((res: any) => {
                if (res) setStatsData(res);
            });
        }
    }, [moduleName]);

    // Reset filters on module change
    const prevModuleRef = useRef(moduleName);
    useEffect(() => {
        if (prevModuleRef.current !== moduleName) {
            prevModuleRef.current = moduleName;
            replaceFilters({page: 1, perPage: 20, search: '', strip: 'total', dateFrom: '', dateTo: '', city: '', agent: ''});
        }
    }, [moduleName]);

    useEffect(() => {
        props.actions.getUserPermissionsAndResponsibilities();
        fetchArchive();
    }, [moduleName, props.location.search]);

    // Dashboard stats
    const dashboardStats = useMemo(() => {
        if (!isContact) return [];
        const s = statsData;
        return [
            {key: 'total', label: translatedText({id:"totalArchived",defaultMessage:"Total arkivuar"}), value: s?.total ?? props.archiveRecordsPagination?.total ?? 0, icon: 'users'},
            {key: 'new', label: translatedText({id:"newInArchive",defaultMessage:"Të rinjtë në arkivë"}), value: s?.newThisMonth ?? '\u2014', icon: 'userPlus'},
            {key: 'cities', label: translatedText({id:"topCities",defaultMessage:"Qytetet kryesore"}), value: Object.keys(s?.cityDistribution || {}).length || '\u2014', icon: 'map'},
            {key: 'agents', label: translatedText({id:"agents",defaultMessage:"Agjentët"}), value: (s?.agentDistribution || []).length || '\u2014', icon: 'agent'},
        ];
    }, [statsData, props.archiveRecordsPagination, isContact]);

    const cityDistribution = useMemo(() => {
        const dist = statsData?.cityDistribution;
        if (!dist) return [];
        return Object.entries(dist).sort((a: any, b: any) => b[1] - a[1]);
    }, [statsData]);

    // Position popover under a card
    const positionPopoverUnderCard = (cardIndex: number) => {
        if (!stripRef.current) return {top: 0, right: 0};
        const cards = stripRef.current.querySelectorAll('.cds-card');
        const card = cards[cardIndex];
        if (!card) return {top: 0, right: 0};
        const rect = card.getBoundingClientRect();
        const popoverWidth = 220;
        const cardCenter = rect.left + rect.width / 2;
        return {top: rect.bottom + 4, right: window.innerWidth - cardCenter - popoverWidth / 2};
    };

    // Strip click
    const handleStripClick = (key: string) => {
        if (key === 'cities') {
            setAgentPopoverOpen(false);
            if (!cityPopoverOpen) setCityPopoverPos(positionPopoverUnderCard(2));
            setCityPopoverOpen(!cityPopoverOpen);
            return;
        }
        if (key === 'agents') {
            setCityPopoverOpen(false);
            if (!agentPopoverOpen) setAgentPopoverPos(positionPopoverUnderCard(3));
            setAgentPopoverOpen(!agentPopoverOpen);
            return;
        }
        setCityPopoverOpen(false);
        setAgentPopoverOpen(false);
        setFilters({city: '', agent: '', strip: key === stripFilter ? 'total' : key, page: 1, search: ''});
    };

    const handleCityFilter = (city: string) => {
        setFilters({city: city, agent: '', strip: 'cities', page: 1});
        setCityPopoverOpen(false);
    };

    const handleAgentFilter = (agent: {id: number; name: string}) => {
        setFilters({agent: String(agent.id), city: '', strip: 'agents', page: 1});
        setAgentPopoverOpen(false);
    };

    const clearCityFilter = () => setFilters({city: '', strip: 'total', page: 1});
    const clearAgentFilter = () => setFilters({agent: '', strip: 'total', page: 1});

    // Close popovers on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (cityRef.current && !cityRef.current.contains(e.target as Node)) setCityPopoverOpen(false);
            if (agentRef.current && !agentRef.current.contains(e.target as Node)) setAgentPopoverOpen(false);
        };
        if (cityPopoverOpen || agentPopoverOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [cityPopoverOpen, agentPopoverOpen]);

    // Edit routing
    const editArchiveData = useCallback((record: any) => {
        if (moduleName === 'contact') history.push({pathname: `/dashboard/${moduleName}/create/${record?.id}`});
        else if (moduleName === 'warehouse') history.push({pathname: `/dashboard/${moduleName}/create/${record?.client?.id}`});
        else if (moduleName === 'transport') history.push({pathname: `/dashboard/${moduleName}/create/${record?.code}`});
        else if (moduleName === 'installation') history.push({pathname: `/dashboard/${moduleName}/create/${record?.project.value}`});
        else if (moduleName === 'measures') history.push({pathname: `/dashboard/${moduleName}/create/${record?.project.value}/${record?.clientId}`});
        else history.push({pathname: `/dashboard/${moduleName}/create/${record?.id}/${record?.project?.value}`});
    }, [moduleName, history]);

    // Delete
    const handleRemove = (record: any) => { setDeleteId(record?.id); setDeleteRecord(record); };
    const confirmDelete = () => {
        let endpoint = moduleName;
        if (moduleName === 'installation') endpoint = 'installations';
        else if (moduleName === 'service') endpoint = 'servicing';
        else if (moduleName === 'contact') endpoint = 'clients';
        props.actions.deleteArchiveData(endpoint, deleteId).then(() => fetchArchive());
        setDeleteId(null);
        setDeleteRecord(null);
    };

    // Search
    const handleSearch = (term: string) => {
        replaceFilters({search: term, page: 1});
    };

    // Date range filter
    const handleDateChange = (range: DateRange | null) => {
        const updates: any = {dateFrom: range?.from || '', dateTo: range?.to || '', page: 1};
        if (stripFilter === 'new') updates.strip = 'total';
        setFilters(updates);
    };

    // CSV export
    const handleExport = () => {
        const getNestedValue = (obj: any, path: string) => path.split('.').reduce((acc, part) => acc?.[part], obj);
        const header = columns.map(c => c.title).join(',');
        const rows = (props?.archiveRecords || []).map((row: any) =>
            columns.map(c => { const val = getNestedValue(row, c.key) || ''; return `"${String(val).replace(/"/g, '""')}"`; }).join(',')
        );
        const csv = [header, ...rows].join('\n');
        const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `arkiva_${moduleName}.csv`;
        link.click();
    };

    const tableActions = [
        {
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
            tooltip: translatedText({id:"edit",defaultMessage:"Ndrysho"}), onClick: editArchiveData
        },
        {
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>,
            tooltip: translatedText({id:"delete",defaultMessage:"Fshi"}), onClick: handleRemove
        }
    ];

    return (
        <div className="clp-page">
            <MikaPageHeader
                title={MODULE_TITLES[moduleName] || translatedText({id:"archive",defaultMessage:"Arkiva"})}
                subtitle={props.archiveRecordsPagination ? `${props.archiveRecordsPagination.total} total` : undefined}
            />

            {/* Toolbar: search + date filter + view toggle + export */}
            <div className="clp-compact-toolbar">
                <div className="clp-search-wrap">
                    <svg className="clp-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                    </svg>
                    <input
                        type="text" className="clp-search-input" placeholder={translatedText({id:"searchArchive",defaultMessage:"Kërko në arkivë..."})}
                        value={searchText} onChange={(e) => handleSearch(e.target.value)}
                    />
                    {searchText && (
                        <button className="clp-search-clear" onClick={() => handleSearch('')} type="button">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                        </button>
                    )}
                </div>
                <div className="clp-toolbar-actions">
                    <MikaDateFilter value={dateRange} onChange={handleDateChange} />
                    {isContact && <MikaViewToggle mode={viewMode} onChange={(m) => setViewMode(m)} />}
                    <button className="clp-export-btn" onClick={handleExport} title="Eksporto CSV" type="button">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                    </button>
                </div>
            </div>

            {/* Contact archive: dashboard strip with city/agent popovers */}
            {isContact && (
                <>
                    <div ref={stripRef}>
                        <ContactDashboardStrip
                            stats={dashboardStats}
                            activeFilter={stripFilter}
                            onFilterClick={handleStripClick}
                        />
                    </div>
                    {cityPopoverOpen && (
                        <div className="clp-city-popover" ref={cityRef} style={{top: cityPopoverPos.top, right: cityPopoverPos.right}}>
                            <div className="clp-city-header">{translatedText({id:"topCities",defaultMessage:"Qytetet kryesore"})}</div>
                            {cityDistribution.length === 0 ? <div className="clp-city-empty">{translatedText({id:"noData",defaultMessage:"Nuk ka të dhëna"})}</div> : (
                                cityDistribution.map(([city, count]) => (
                                    <button key={city} className={`clp-city-row ${activeCity === city ? 'clp-city-row-active' : ''}`} onClick={() => handleCityFilter(city)} type="button">
                                        <span className="clp-city-name">{city}</span><span className="clp-city-count">{count}</span>
                                    </button>
                                ))
                            )}
                        </div>
                    )}
                    {agentPopoverOpen && (
                        <div className="clp-city-popover" ref={agentRef} style={{top: agentPopoverPos.top, right: agentPopoverPos.right}}>
                            <div className="clp-city-header">{translatedText({id:"agents",defaultMessage:"Agjentët"})}</div>
                            {agentDistribution.length === 0 ? <div className="clp-city-empty">{translatedText({id:"noData",defaultMessage:"Nuk ka të dhëna"})}</div> : (
                                agentDistribution.map((agent) => (
                                    <button key={agent.id} className={`clp-city-row ${activeAgent?.id === agent.id ? 'clp-city-row-active' : ''}`} onClick={() => handleAgentFilter(agent)} type="button">
                                        <span className="clp-city-name">{agent.name}</span><span className="clp-city-count">{agent.count}</span>
                                    </button>
                                ))
                            )}
                        </div>
                    )}
                </>
            )}

            {/* Filter chips */}
            {(activeCity || activeAgent) && (
                <div className="clp-active-filter-bar">
                    {activeCity && (
                        <div className="clp-active-filter-chip">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                            <span>{activeCity}</span>
                            <span className="clp-active-filter-count">{props.archiveRecordsPagination?.total ?? 0} {translatedText({id:"clients",defaultMessage:"klientë"})}</span>
                            <button className="clp-active-filter-clear" onClick={clearCityFilter} type="button"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg></button>
                        </div>
                    )}
                    {activeAgent && (
                        <div className="clp-active-filter-chip">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                            <span>{activeAgent.name}</span>
                            <span className="clp-active-filter-count">{props.archiveRecordsPagination?.total ?? 0} {translatedText({id:"clients",defaultMessage:"klientë"})}</span>
                            <button className="clp-active-filter-clear" onClick={clearAgentFilter} type="button"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg></button>
                        </div>
                    )}
                </div>
            )}

            {isContact && viewMode === 'card' ? (
                <ContactCardGrid
                    data={props?.archiveRecords || []}
                    onEdit={editArchiveData}
                    onDelete={handleRemove}
                />
            ) : (
                <MikaTable
                    columns={columns}
                    data={props?.archiveRecords || []}
                    actions={tableActions}
                    loading={props.loading}
                    onRowClick={editArchiveData}
                    emptyMessage={translatedText({id:"noDataInArchive",defaultMessage:"Nuk ka të dhëna në arkivë"})}
                />
            )}

            <PaginationComponent
                currentPage={currentPage}
                perPage={perPage}
                pagination={props.archiveRecordsPagination}
                onPageChange={(page) => setFilter('page', page)}
                onPerPageChange={(value) => setFilters({perPage: value, page: 1})}
                label={MODULE_LABELS[moduleName] || translatedText({id:"archivesPl",defaultMessage:"arkivat"})}
            />

            <MikaConfirmModal
                isOpen={!!deleteRecord}
                onClose={() => { setDeleteId(null); setDeleteRecord(null); }}
                onConfirm={confirmDelete}
                title={translatedText({id:"deleteFromArchive",defaultMessage:"Fshi nga arkiva"})}
                message={translatedText({id:"confirmDeleteArchiveRecord",defaultMessage:"A jeni të sigurt që doni të fshini këtë rekord nga arkiva?"})}
                confirmLabel={translatedText({id:"delete",defaultMessage:"Fshi"})}
                confirmColor="danger"
            />
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return { ...ownProps, ...state.app, client: state.client, measures: state.measures };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        clientActions: bindActionCreators(clientActions as any, dispatch),
        measuresActions: bindActionCreators(measuresActions as any, dispatch),
        actions: bindActionCreators(actions as any, dispatch)
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(Archive)));
